import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Users as UsersIcon, Shield, RotateCcw, ChevronDown, ChevronRight, Pencil, MapPin } from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';
import { PermissionGate } from '../components/PermissionGate';
import { usePermission } from '../hooks/usePermission';
import { useAuth } from '../contexts/AuthContext';
import { isSystemAdmin } from '../lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PermissionPicker({ value, onChange, categories }) {
  const [expanded, setExpanded] = useState({});

  const toggleCategory = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isChecked = (permKey) => value.includes(permKey);

  const toggle = (permKey) => {
    onChange(
      isChecked(permKey) ? value.filter((k) => k !== permKey) : [...value, permKey]
    );
  };

  const toggleAll = (catPerms, checked) => {
    const keys = catPerms.map((p) => p.key);
    if (checked) {
      onChange([...new Set([...value, ...keys])]);
    } else {
      onChange(value.filter((k) => !keys.includes(k)));
    }
  };

  if (!categories?.length) return null;

  return (
    <div className="space-y-1 max-h-[50vh] overflow-y-auto">
      {categories.map((cat) => {
        const catPerms = cat.permissions;
        const checkedCount = catPerms.filter((p) => isChecked(p.key)).length;
        const allChecked = checkedCount === catPerms.length;
        const isOpen = expanded[cat.key] ?? false;

        return (
          <div key={cat.key} className="border rounded-md">
            <button
              type="button"
              onClick={() => toggleCategory(cat.key)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span>{cat.label}</span>
                <span className="text-xs text-muted-foreground">
                  ({checkedCount}/{catPerms.length})
                </span>
              </div>
              <Checkbox
                checked={allChecked}
                onCheckedChange={(checked) => {
                  toggleAll(catPerms, checked);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </button>
            {isOpen && (
              <div className="px-3 pb-2 space-y-1">
                {catPerms.map((perm) => (
                  <div key={perm.key} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/30">
                    <Checkbox
                      id={`perm-${perm.key}`}
                      checked={isChecked(perm.key)}
                      onCheckedChange={() => toggle(perm.key)}
                    />
                    <label htmlFor={`perm-${perm.key}`} className="text-sm cursor-pointer flex-1">
                      {perm.label}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function UsersPage() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // User dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', role_id: '' });
  const [overrides, setOverrides] = useState({});
  const [selectedRoleId, setSelectedRoleId] = useState('');

  // Role dialogs
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', slug: '', description: '', permissions: [] });

  // Hidden system-admin panel. `is_system` only ever arrives on your own
  // /auth/me — the backend strips it from every other user payload — so this
  // tab simply does not exist for anyone else.
  const { user: authUser } = useAuth();
  const amSystemAdmin = isSystemAdmin(authUser);
  const [systemAdmins, setSystemAdmins] = useState([]);
  const [grantCandidates, setGrantCandidates] = useState([]);
  const [grantTargetId, setGrantTargetId] = useState('');

  const loadSystemData = useCallback(async () => {
    if (!amSystemAdmin) return;
    try {
      const [adminsRes, candidatesRes] = await Promise.all([
        axios.get(`${API}/system/admins`, { withCredentials: true }),
        axios.get(`${API}/system/candidates`, { withCredentials: true }),
      ]);
      setSystemAdmins(adminsRes.data);
      setGrantCandidates(candidatesRes.data);
    } catch (error) {
      toast.error('Failed to load system admins');
    }
  }, [amSystemAdmin]);

  useEffect(() => {
    loadSystemData();
  }, [loadSystemData]);

  const handleGrantSystem = async () => {
    if (!grantTargetId) return;
    try {
      await axios.post(`${API}/system/admins/${grantTargetId}`, {}, { withCredentials: true });
      toast.success('System admin granted');
      setGrantTargetId('');
      loadSystemData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant');
    }
  };

  const handleRevokeSystem = async (userId) => {
    if (!window.confirm('Revoke system admin from this user?')) return;
    try {
      await axios.delete(`${API}/system/admins/${userId}`, { withCredentials: true });
      toast.success('System admin revoked');
      loadSystemData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke');
    }
  };

  // Data scope dialog
  const [showScopeDialog, setShowScopeDialog] = useState(false);
  const [scopeUser, setScopeUser] = useState(null);
  const [scopeConstituencies, setScopeConstituencies] = useState([]);
  const [scopeCampaigns, setScopeCampaigns] = useState([]);
  const [allConstituencies, setAllConstituencies] = useState([]);
  const [campaignsByConstituency, setCampaignsByConstituency] = useState({});

  const canManageScopes = usePermission('users.manage_scopes');
  const canManageRoles = usePermission('users.manage_roles');
  const canManagePermissions = usePermission('users.manage_permissions');
  const canCreateUsers = usePermission('users.create');
  const canDeleteUsers = usePermission('users.delete');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, configRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/users/permissions-config`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setRoles(configRes.data.roles || []);
      setCategories(configRes.data.categories || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---- User actions ----

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.role_id) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await axios.post(`${API}/users`, newUser, { withCredentials: true });
      toast.success('User created');
      setShowAddDialog(false);
      setNewUser({ name: '', email: '', role_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleUpdatePermissions = async () => {
    try {
      await axios.put(
        `${API}/users/${selectedUser.user_id}/permissions`,
        { user_id: selectedUser.user_id, permission_overrides: overrides },
        { withCredentials: true }
      );
      toast.success('Permission overrides updated');
      setShowPermissionsDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const openPermissions = (user) => {
    setSelectedUser(user);
    setSelectedRoleId(user.role_id || '');
    setOverrides(user.permission_overrides || {});
    setShowPermissionsDialog(true);
  };

  // ---- Data scope ----

  const openScopes = async (user) => {
    setScopeUser(user);
    setScopeConstituencies(user.scopes?.constituencies || []);
    setScopeCampaigns(user.scopes?.campaigns || []);
    setShowScopeDialog(true);
    try {
      const res = await axios.get(`${API}/constituencies`, { withCredentials: true });
      setAllConstituencies(res.data);
      // Load campaigns for every constituency so they can be narrowed inline
      const lists = await Promise.all(
        res.data.map((c) =>
          axios
            .get(`${API}/campaigns?constituency_id=${c.constituency_id}`, { withCredentials: true })
            .then((r) => [c.constituency_id, r.data])
            .catch(() => [c.constituency_id, []])
        )
      );
      setCampaignsByConstituency(Object.fromEntries(lists));
    } catch {
      toast.error('Failed to load constituencies');
    }
  };

  const toggleScopeConstituency = (id) => {
    setScopeConstituencies((prev) => {
      if (prev.includes(id)) {
        // Dropping a constituency also drops any campaign narrowing under it,
        // which would otherwise linger as dead ids in the saved scope
        const theirs = (campaignsByConstituency[id] || []).map((c) => c.campaign_id);
        setScopeCampaigns((cs) => cs.filter((x) => !theirs.includes(x)));
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const toggleScopeCampaign = (campaignId) => {
    setScopeCampaigns((prev) =>
      prev.includes(campaignId) ? prev.filter((x) => x !== campaignId) : [...prev, campaignId]
    );
  };

  const handleSaveScopes = async () => {
    if (!scopeUser) return;
    try {
      await axios.put(
        `${API}/users/${scopeUser.user_id}/scopes`,
        { constituencies: scopeConstituencies, campaigns: scopeCampaigns },
        { withCredentials: true }
      );
      toast.success('Data access updated');
      setShowScopeDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update data access');
    }
  };

  const handleRoleChange = async (newRoleId) => {
    if (!selectedUser || newRoleId === selectedRoleId) return;
    const role = roles.find((r) => r.role_id === newRoleId);
    if (!role) return;

    try {
      await axios.put(
        `${API}/users/${selectedUser.user_id}/role`,
        { role_id: newRoleId, reset_permissions: true },
        { withCredentials: true }
      );
      setSelectedRoleId(newRoleId);
      setOverrides({});
      toast.success(`Role updated to ${role.name}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleResetOverrides = () => {
    if (!window.confirm('Clear all permission overrides for this user?')) return;
    setOverrides({});
    toast.info('Overrides cleared — click "Save Overrides" to apply');
  };

  // ---- Role actions ----

  const openNewRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', slug: '', description: '', permissions: [] });
    setShowRoleDialog(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      slug: role.slug,
      description: role.description || '',
      permissions: [...role.permissions],
    });
    setShowRoleDialog(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name) {
      toast.error('Role name is required');
      return;
    }
    try {
      if (editingRole) {
        await axios.put(
          `${API}/roles/${editingRole.role_id}`,
          { name: roleForm.name, description: roleForm.description, permissions: roleForm.permissions },
          { withCredentials: true }
        );
        toast.success('Role updated');
      } else {
        if (!roleForm.slug) {
          toast.error('Slug is required');
          return;
        }
        await axios.post(`${API}/roles`, roleForm, { withCredentials: true });
        toast.success('Role created');
      }
      setShowRoleDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Delete this custom role?')) return;
    try {
      await axios.delete(`${API}/roles/${roleId}`, { withCredentials: true });
      toast.success('Role deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // ---- Helpers ----

  const getRoleName = (user) => {
    const role = roles.find((r) => r.role_id === user.role_id);
    return role?.name || user.role_name || user.role_slug?.replace(/_/g, ' ') || user.role?.replace(/_/g, ' ') || 'Unknown';
  };

  const getRoleBadgeColor = (slug) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-700',
      admin: 'bg-blue-100 text-blue-700',
      manager: 'bg-teal-100 text-teal-700',
      viewer: 'bg-gray-100 text-gray-700',
      call_center: 'bg-orange-100 text-orange-700',
      box_agent: 'bg-green-100 text-green-700',
    };
    return colors[slug] || 'bg-indigo-100 text-indigo-700';
  };

  const assignableRoles = roles.filter((r) => r.slug !== 'super_admin');

  const overrideKeys = Object.keys(overrides);
  const grantedOverrides = overrideKeys.filter((k) => overrides[k] === true);
  const deniedOverrides = overrideKeys.filter((k) => overrides[k] === false);

  return (
    <Layout>
      <div className="p-4 md:p-8" data-testid="users-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Users & Roles
            </h1>
            <p className="text-muted-foreground">Manage user accounts, roles, and permissions</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="roles">Roles</TabsTrigger>
            {amSystemAdmin && <TabsTrigger value="system">System</TabsTrigger>}
          </TabsList>

          {/* ==================== USERS TAB ==================== */}
          <TabsContent value="users">
            <div className="flex justify-end mb-4">
              <PermissionGate permission="users.create">
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-user-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New User</DialogTitle>
                      <DialogDescription>User will login with Google OAuth</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={newUser.name}
                          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <Label>Role</Label>
                        <Select value={newUser.role_id} onValueChange={(v) => setNewUser({ ...newUser, role_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Select a role" /></SelectTrigger>
                          <SelectContent>
                            {assignableRoles.map((role) => (
                              <SelectItem key={role.role_id} value={role.role_id}>{role.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleCreateUser} className="w-full">Create User</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </PermissionGate>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : users.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No users yet</h3>
                  <p className="text-muted-foreground mb-4">Add your first user</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.map((u) => {
                  const slug = u.role_slug || u.role;
                  return (
                    <Card key={u.user_id} data-testid="user-card">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <UserAvatar user={u} size="lg" />
                            <div>
                              <h3 className="font-semibold">{u.name}</h3>
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(slug)}`}>
                                {getRoleName(u)}
                              </span>
                            </div>
                          </div>
                          {slug !== 'super_admin' && canDeleteUsers && (
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(u.user_id)} data-testid="delete-user-button">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2 text-sm mb-4">
                          <div>
                            <span className="text-muted-foreground">Email:</span>
                            <p className="font-medium">{u.email}</p>
                          </div>
                        </div>
                        {slug !== 'super_admin' && canManagePermissions && (
                          <Button size="sm" variant="outline" className="w-full" onClick={() => openPermissions(u)}>
                            <Shield className="h-4 w-4 mr-2" />
                            Manage Permissions
                          </Button>
                        )}
                        {slug !== 'super_admin' && canManageScopes && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full mt-2"
                            onClick={() => openScopes(u)}
                          >
                            <MapPin className="h-4 w-4 mr-2" />
                            Data Access
                            <span className="ml-2 text-xs text-muted-foreground">
                              {(u.scopes?.constituencies?.length ?? 0) === 0
                                ? 'none'
                                : `${u.scopes.constituencies.length} ${
                                    u.scopes.constituencies.length === 1
                                      ? 'constituency'
                                      : 'constituencies'
                                  }`}
                            </span>
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ==================== ROLES TAB ==================== */}
          <TabsContent value="roles">
            <div className="flex justify-end mb-4">
              {canManageRoles && (
                <Button onClick={openNewRole}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Custom Role
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map((role) => (
                <Card key={role.role_id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{role.name}</h3>
                        <span className="text-xs text-muted-foreground font-mono">{role.slug}</span>
                      </div>
                      <div className="flex gap-1">
                        {role.is_system_role ? (
                          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">System</span>
                        ) : (
                          canManageRoles && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openEditRole(role)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteRole(role.role_id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{role.description || 'No description'}</p>
                    <div className="text-xs text-muted-foreground">
                      {role.slug === 'super_admin'
                        ? 'All permissions (bypass)'
                        : `${role.permissions?.length || 0} permissions`}
                    </div>
                    {canManageRoles && role.slug !== 'super_admin' && (
                      <Button size="sm" variant="outline" className="w-full mt-3" onClick={() => openEditRole(role)}>
                        <Shield className="h-4 w-4 mr-2" />
                        Edit Permissions
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ==================== SYSTEM TAB (system admins only) ==================== */}
          {amSystemAdmin && (
            <TabsContent value="system">
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-1">System Admins</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Hidden maintainer access. Each of these accounts appears to everyone
                    else as whatever their visible role says. This tab, the flag itself,
                    and any grant or revoke below are invisible to non-system admins.
                  </p>

                  <div className="space-y-2 mb-6">
                    {systemAdmins.map((a) => (
                      <div
                        key={a.user_id}
                        className="flex items-center justify-between border rounded-lg px-4 py-3"
                      >
                        <div>
                          <div className="font-medium">{a.name}</div>
                          <div className="text-sm text-muted-foreground">{a.email}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Appears as: {a.role_slug || 'unknown'}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRevokeSystem(a.user_id)}
                        >
                          Revoke
                        </Button>
                      </div>
                    ))}
                    {systemAdmins.length === 0 && (
                      <p className="text-sm text-muted-foreground">No system admins.</p>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <Label className="mb-2 block">Grant system admin</Label>
                    <div className="flex gap-2">
                      <Select value={grantTargetId} onValueChange={setGrantTargetId}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select a user" />
                        </SelectTrigger>
                        <SelectContent>
                          {grantCandidates.map((c) => (
                            <SelectItem key={c.user_id} value={c.user_id}>
                              {c.name} ({c.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleGrantSystem} disabled={!grantTargetId}>
                        Grant
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Their visible role does not change — only hidden access is added.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* ==================== DATA SCOPE DIALOG ==================== */}
        <Dialog open={showScopeDialog} onOpenChange={setShowScopeDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Data Access: {scopeUser?.name}</DialogTitle>
              <DialogDescription>
                Which constituencies this user may work in. Roles decide what they can
                do; this decides where. With none selected they see no data at all.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 max-h-80 overflow-y-auto py-2">
              {allConstituencies.map((c) => {
                const granted = scopeConstituencies.includes(c.constituency_id);
                const campaigns = campaignsByConstituency[c.constituency_id] || [];
                const narrowed = campaigns.filter((cam) =>
                  scopeCampaigns.includes(cam.campaign_id)
                );
                return (
                  <div key={c.constituency_id} className="border rounded-lg">
                    <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50">
                      <Checkbox
                        checked={granted}
                        onCheckedChange={() => toggleScopeConstituency(c.constituency_id)}
                      />
                      <span className="text-sm font-medium">{c.name}</span>
                      {granted && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          {narrowed.length === 0
                            ? 'all campaigns'
                            : `${narrowed.length} of ${campaigns.length} campaigns`}
                        </span>
                      )}
                    </label>

                    {granted && campaigns.length > 0 && (
                      <div className="border-t px-3 py-2 space-y-1 bg-muted/20">
                        <p className="text-xs text-muted-foreground mb-1">
                          Tick campaigns to restrict to those only. Leave all unticked
                          for access to every campaign here.
                        </p>
                        {campaigns.map((cam) => (
                          <label
                            key={cam.campaign_id}
                            className="flex items-center gap-3 py-1 cursor-pointer"
                          >
                            <Checkbox
                              checked={scopeCampaigns.includes(cam.campaign_id)}
                              onCheckedChange={() => toggleScopeCampaign(cam.campaign_id)}
                            />
                            <span className="text-sm">{cam.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {allConstituencies.length === 0 && (
                <p className="text-sm text-muted-foreground">No constituencies available.</p>
              )}
            </div>

            {scopeConstituencies.length === 0 && (
              <p className="text-sm text-amber-600">
                No constituencies selected — this user will not see any data.
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowScopeDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveScopes}>Save</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ==================== USER PERMISSIONS DIALOG ==================== */}
        <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Manage Permissions: {selectedUser?.name}
              </DialogTitle>
              <DialogDescription>
                Change role or add/remove individual permission overrides
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-3 pb-3 border-b">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1 block">Role</Label>
                <Select value={selectedRoleId} onValueChange={handleRoleChange}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map((role) => (
                      <SelectItem key={role.role_id} value={role.role_id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="pt-4">
                <Button size="sm" variant="outline" onClick={handleResetOverrides}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Clear Overrides
                </Button>
              </div>
            </div>

            <div className="space-y-3 mt-2">
              <p className="text-sm text-muted-foreground">
                Toggle permissions to override the role defaults.
                {overrideKeys.length > 0 && (
                  <span className="ml-1 font-medium text-foreground">
                    {grantedOverrides.length} granted, {deniedOverrides.length} denied overrides.
                  </span>
                )}
              </p>

              <PermissionPicker
                value={
                  (() => {
                    const role = roles.find((r) => r.role_id === selectedRoleId);
                    const base = new Set(role?.permissions || []);
                    for (const [k, v] of Object.entries(overrides)) {
                      if (v) base.add(k); else base.delete(k);
                    }
                    return [...base];
                  })()
                }
                onChange={(newPerms) => {
                  const role = roles.find((r) => r.role_id === selectedRoleId);
                  const basePerms = new Set(role?.permissions || []);
                  const newOverrides = {};
                  const allKeys = new Set([...basePerms, ...newPerms]);
                  for (const k of allKeys) {
                    const inRole = basePerms.has(k);
                    const inNew = newPerms.includes(k);
                    if (inRole && !inNew) newOverrides[k] = false;
                    else if (!inRole && inNew) newOverrides[k] = true;
                  }
                  setOverrides(newOverrides);
                }}
                categories={categories}
              />
            </div>

            <Button onClick={handleUpdatePermissions} className="w-full mt-4">
              Save Overrides
            </Button>
          </DialogContent>
        </Dialog>

        {/* ==================== ROLE EDIT/CREATE DIALOG ==================== */}
        <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRole ? `Edit Role: ${editingRole.name}` : 'Create Custom Role'}</DialogTitle>
              <DialogDescription>
                {editingRole
                  ? 'Update the name, description, or permissions for this role'
                  : 'Define a new role with a set of permissions'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="Role name" />
              </div>
              {!editingRole && (
                <div>
                  <Label>Slug</Label>
                  <Input
                    value={roleForm.slug}
                    onChange={(e) => setRoleForm({ ...roleForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    placeholder="role_slug"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Unique identifier (lowercase, underscores). Cannot be changed later.</p>
                </div>
              )}
              <div>
                <Label>Description</Label>
                <Input value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} placeholder="Optional description" />
              </div>
              <div>
                <Label className="mb-2 block">Permissions</Label>
                <PermissionPicker
                  value={roleForm.permissions}
                  onChange={(perms) => setRoleForm({ ...roleForm, permissions: perms })}
                  categories={categories}
                />
              </div>
              <Button onClick={handleSaveRole} className="w-full">
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
